import React, { useEffect, useMemo, useRef, useState } from "react";
import { geoNaturalEarth1, geoPath } from "d3-geo";
import { getDashboardBuildingInformation } from "../services/dashboardService";

const WorldMap = () => {
  const [worldGeos, setWorldGeos] = useState([]);
  const [mapLoading, setMapLoading] = useState(true);
  const [buildingStatsByIso, setBuildingStatsByIso] = useState({});
  const [tooltip, setTooltip] = useState({
    visible: false,
    text: "",
    x: 0,
    y: 0,
  });
  const mapRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setMapLoading(true);
        const geoRes = await fetch(
          "https://raw.githubusercontent.com/holtzy/D3-graph-gallery/master/DATA/world.geojson",
        );
        const geo = await geoRes.json();
        if (mounted) setWorldGeos(geo.features || []);
      } catch {
        if (mounted) setWorldGeos([]);
      } finally {
        if (mounted) setMapLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await getDashboardBuildingInformation();
        const list = Array.isArray(data) ? data : [];
        const stats = {};
        for (const b of list) {
          const iso3 = b.countryIso3;
          if (!iso3 || iso3 === "-99") continue;
          if (!stats[iso3]) {
            stats[iso3] = {
              count: 0,
              countryName: b.countryName || "",
              buildings: [],
            };
          }
          stats[iso3].count += 1;
          const cityName = b.cityName || "";
          const buildingName = b.name || "";
          if (buildingName) {
            stats[iso3].buildings.push({
              name: buildingName,
              city: cityName,
            });
          }
        }
        if (!mounted) return;
        const normalized = {};
        Object.keys(stats).forEach((k) => {
          normalized[k] = {
            count: stats[k].count,
            countryName: stats[k].countryName,
            buildings: stats[k].buildings,
          };
        });
        setBuildingStatsByIso(normalized);
      } catch {
        if (mounted) setBuildingStatsByIso({});
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const mapSize = useMemo(() => ({ width: 900, height: 520 }), []);
  const projection = useMemo(
    () =>
      geoNaturalEarth1()
        .scale(165)
        .translate([mapSize.width / 2, mapSize.height / 2]),
    [mapSize],
  );
  const pathGenerator = useMemo(() => geoPath(projection), [projection]);

  return (
    <div className="role-card" style={{ marginTop: 20 }}>
      <div
        className="section-title"
        style={{ marginBottom: 12, fontWeight: 600 }}
      >
        World Map
      </div>
      {mapLoading ?
        <div className="no-results">Loading map...</div>
      : <div
          ref={mapRef}
          style={{
            position: "relative",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <svg
            viewBox={`0 0 ${mapSize.width} ${mapSize.height}`}
            width="100%"
            // Scale with the card on narrow laptops instead of letterboxing
            // inside a fixed 520px box; never grow taller than the design size.
            style={{
              display: "block",
              height: "auto",
              maxHeight: mapSize.height,
              background: "#f8fafc",
              borderRadius: 12,
            }}
          >
            <g>
              {worldGeos.map((geo) => {
                const name = geo?.properties?.name || "Unknown";
                const iso3 =
                  geo?.id ||
                  geo?.properties?.iso_a3 ||
                  geo?.properties?.ISO_A3 ||
                  "";
                const stat = iso3 ? buildingStatsByIso[iso3] : null;
                const isoLabel = iso3 && iso3 !== "-99" ? iso3 : "";
                const header = `${stat?.countryName || name}${
                  isoLabel ? ` (${isoLabel})` : ""
                }`;
                const countLine =
                  typeof stat?.count === "number" ?
                    `Buildings: ${stat.count}`
                  : "Buildings: 0";
                const buildingLines =
                  Array.isArray(stat?.buildings) ?
                    stat.buildings.map(
                      (b) => `${b.name}${b.city ? ` (${b.city})` : ""}`,
                    )
                  : [];
                const label =
                  stat ?
                    [header, countLine, ...buildingLines].join("\n")
                  : `${name}${isoLabel ? ` (${isoLabel})` : ""}`;
                const hasBuildings =
                  typeof stat?.count === "number" && stat.count > 0;
                return (
                  <path
                    key={`${geo.id}-${geo?.properties?.name || ""}`}
                    d={pathGenerator(geo)}
                    fill={hasBuildings ? "#255de0" : "#e2e8f0"}
                    stroke="#94a3b8"
                    strokeWidth={1}
                    onMouseMove={(e) => {
                      const rect = mapRef.current?.getBoundingClientRect();
                      const x = rect ? e.clientX - rect.left + 12 : e.clientX;
                      const y = rect ? e.clientY - rect.top + 12 : e.clientY;
                      setTooltip({ visible: true, text: label, x, y });
                    }}
                    onMouseLeave={() =>
                      setTooltip({ visible: false, text: "", x: 0, y: 0 })
                    }
                  />
                );
              })}
            </g>
          </svg>
          {tooltip.visible && (
            <div
              style={{
                position: "absolute",
                left: tooltip.x,
                top: tooltip.y,
                background: "#111827",
                color: "#f9fafb",
                padding: "6px 10px",
                borderRadius: 8,
                fontSize: 12,
                pointerEvents: "none",
                boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
                whiteSpace: "pre-line",
              }}
            >
              {tooltip.text}
            </div>
          )}
        </div>
      }
    </div>
  );
};

export default WorldMap;
